import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../api';
import AuthBackground from '../../components/AuthBackground';
import TextField from '../../components/TextField';
import Button from '../../components/Button';
import Tabs from '../../components/Tabs';
// import SocialRow from '../../components/SocialRow';
import Toast from '../../components/Toast';
import PasswordRules from '../../components/PasswordRules';

const Auth = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') === 'signup' ? 'Sign Up' : 'Log In';
  
  const [activeTab, setActiveTab] = useState(initialTab);
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const validatePassword = (password) => {
    return password.length >= 8 && /\d/.test(password) && /[a-zA-Z]/.test(password);
  };

  const handleLogin = async () => {
    const newErrors = {};
    if (!validateEmail(loginForm.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    if (!validatePassword(loginForm.password)) {
      newErrors.password = 'Password must be at least 8 characters with letters and numbers';
    }
    
    setErrors(newErrors);
    if (Object.keys(newErrors).length === 0) {
      setLoading(true);
      try {
        const response = await api.post('/accounts/login/', loginForm);

        const data = response.data;

        if (response.status === 200) {
          localStorage.setItem('access', data.tokens.access);
          localStorage.setItem('refreshToken', data.tokens.refresh);
          localStorage.setItem('user', JSON.stringify(data.user));

          setToast({ type: 'success', message: 'Login successful!' });

          if (data.user.user_type === 'ADMIN') {
            setTimeout(() => navigate('/admin-dashboard'), 1500);
          } else if (data.user.profile?.is_onboarding_complete) {
            setTimeout(() => navigate('/client-dashboard'), 1500);
          } else {
            setTimeout(() => navigate('/onboarding'), 1500);
          }
        }
      } catch (error) {
        let errorMessage = 'An unexpected error occurred. Please try again.';
        if (error.response && error.response.data) {
          const errorData = error.response.data;
          if (typeof errorData === 'string') {
            errorMessage = errorData;
          } else if (errorData.detail) {
            errorMessage = errorData.detail;
          } else if (typeof errorData === 'object') {
            errorMessage = Object.values(errorData).flat().join(' ');
          }
        }
        setToast({ type: 'error', message: errorMessage });
      } finally {
        setLoading(false);
      }
    }
  };

  const handleTabChange = (tab) => {
    if (tab === 'Sign Up') {
      navigate('/signup');
    } else {
      setActiveTab(tab);
    }
  };

  return (
    <>
      <AuthBackground />
      <div className="relative z-10 min-h-screen flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md lg:max-w-lg">
          <div className="bg-transparent lg:bg-white lg:rounded-3xl lg:shadow-xl p-6 lg:p-10">
            <div className="text-center mb-6">
              <h1 className="text-3xl lg:text-4xl font-bold text-[#0C1D40]">Hey Welcome!</h1>
            </div>

            <Tabs 
              tabs={['Log In', 'Sign Up']} 
              activeTab={activeTab} 
              onChange={handleTabChange} 
              className="mb-6"
            />

            <div className="space-y-4">
              <TextField
                placeholder="Email"
                type="email"
                value={loginForm.email}
                onChange={(e) => setLoginForm({...loginForm, email: e.target.value})}
                error={errors.email}
              />
              <div className="space-y-1">
                <TextField
                  placeholder="Password"
                  type="password"
                  value={loginForm.password}
                  onChange={(e) => setLoginForm({...loginForm, password: e.target.value})}
                  error={errors.password}
                />
                <PasswordRules password={loginForm.password} />
              </div>
              <div className="text-right">
                <button onClick={() => navigate('/auth/forgot-password')} className="text-pink-500 hover:underline text-sm">
                  Forgot Password?
                </button>
              </div>
              <Button 
                fullWidth 
                onClick={handleLogin}
                loading={loading}
                disabled={!loginForm.email || !loginForm.password}
              >
                Log In
              </Button>
              
              <div className="flex items-center gap-4 my-6">
                <hr className="flex-1 border-gray-300" />
                <span className="text-gray-500 text-sm">Or continue with</span>
                <hr className="flex-1 border-gray-300" />
              </div>
              
              {/* <SocialRow /> */}
              
              <div className="text-center mt-6">
                <span className="text-gray-600">Don't have an account? </span>
                <button onClick={() => navigate('/signup')} className="text-pink-500 hover:underline font-semibold">
                  Sign Up
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Toast 
        message={toast?.message} 
        type={toast?.type}
        onClose={() => setToast(null)}
      />
    </>
  );
};

export default Auth;
